const DEFAULT_BPM = 120;
const DEFAULT_TOLERANCE = 10;

const DEFAULT_MAX_VALUE = 1000;

export const ALL_EFFECT_TITLES = [
  "Swap",
  "Randomize",
  "Remove",
  "Cut",
  "Repeat",
  "Silence",
  "Reverse",
] as const;

export const MAX_EFFECTS = 6;

export type BpmSettings = {
  isManualTempo: boolean;
  bpm: number;
  isBpmValid: boolean;
  tolerance: number;
  isToleranceValid: boolean;
  bpmValidationMessage?: string;
  toleranceValidationMessage?: string;
};

export type EffectTitleTuple = typeof ALL_EFFECT_TITLES;

export type EffectTitle = EffectTitleTuple[number];

export type EffectType =
  | "swap"
  | "randomize"
  | "remove"
  | "cut"
  | "repeat"
  | "silence"
  | "reverse";

export type EffectParameter = {
  title: string;
  value: number;
  jsonKey: string;
  minValue: number;
};

export type EffectSetting = {
  title: EffectTitle;
  type: EffectType;
  description: string;
  parameters: EffectParameter[];
  isValid: boolean;
  validate: () => ValidationResult;
  errorMessages: string[];
};

type ValidationResult = {
  isSuccess: boolean;
  errorMessages: string[];
};

export const buildEffect = (effectType: EffectTitle) => {
  switch (effectType) {
    case "Swap":
      return buildSwapEffect();
    case "Randomize":
      return buildRandomizeEffect();
    case "Remove":
      return buildRemoveEffect();
    case "Cut":
      return buildCutEffect();
    case "Repeat":
      return buildRepeatEffect();
    case "Silence":
      return buildSilenceEffect();
    case "Reverse":
      return buildReverseEffect();
  }
};

const defaultValidation = (effect: EffectSetting): ValidationResult => {
  let isSuccess = true;
  const errorMessages: string[] = [];

  effect.parameters.forEach((parameter) => {
    if (
      parameter.value < parameter.minValue ||
      parameter.value > DEFAULT_MAX_VALUE
    ) {
      isSuccess = false;
      errorMessages.push(
        `The value of the parameter "${parameter.title}" is out of range. Put it in the range [${parameter.minValue}, ${DEFAULT_MAX_VALUE}].`,
      );
    }
  });

  return {
    isSuccess,
    errorMessages,
  };
};

export const buildSwapEffect = (): EffectSetting => ({
  title: "Swap",
  type: "swap",
  description: "Swaps two beats throughout the entire song.",
  parameters: [
    {
      title: "Every",
      value: 2,
      jsonKey: "x_period",
      minValue: 1,
    },
    {
      title: "With",
      value: 4,
      jsonKey: "y_period",
      minValue: 1,
    },
    {
      title: "Offset",
      value: 0,
      jsonKey: "offset",
      minValue: 0,
    },
  ],
  isValid: true,
  validate: function () {
    let { isSuccess, errorMessages } = defaultValidation(this);

    const everyParam = this.parameters.find(
      (item) => item.jsonKey === "x_period",
    );
    const withParam = this.parameters.find(
      (item) => item.jsonKey === "y_period",
    );

    if (everyParam!.value === withParam!.value) {
      isSuccess = false;
      errorMessages.push("Can't swap a beat with itself!");
    }

    return {
      isSuccess,
      errorMessages,
    };
  },
  errorMessages: [],
});

export const buildRandomizeEffect = (): EffectSetting => ({
  title: "Randomize",
  type: "randomize",
  description: "Totally randomizes the order of all beats.",
  parameters: [],
  isValid: true,
  validate: function () {
    return defaultValidation(this);
  },
  errorMessages: [],
});

export const buildRemoveEffect = (): EffectSetting => ({
  title: "Remove",
  type: "remove",
  description: "Removes beats entirely.",
  parameters: [
    {
      title: "Every",
      value: 2,
      jsonKey: "period",
      minValue: 2,
    },
    { title: "Offset", value: 0, jsonKey: "offset", minValue: 0 },
  ],
  isValid: true,
  validate: function () {
    return defaultValidation(this);
  },
  errorMessages: [],
});

export const buildCutEffect = (): EffectSetting => ({
  title: "Cut",
  type: "cut",
  description:
    "Cuts beats into pieces and keeps one of them (i.e. pieces = 2, piece to keep = 2 takes the second half of each beat).",
  parameters: [
    { title: "Every", value: 2, jsonKey: "period", minValue: 1 },
    { title: "Offset", value: 0, jsonKey: "offset", minValue: 0 },
    { title: "Pieces", value: 2, jsonKey: "denominator", minValue: 2 },
    { title: "Piece to Keep", value: 1, jsonKey: "take_index", minValue: 1 },
  ],
  isValid: true,
  validate: function () {
    let { isSuccess, errorMessages } = defaultValidation(this);

    const piecesParam = this.parameters.find(
      (item) => item.jsonKey === "denominator",
    );
    const pieceToKeepParam = this.parameters.find(
      (item) => item.jsonKey === "take_index",
    );

    if (piecesParam!.value < pieceToKeepParam!.value) {
      isSuccess = false;
      errorMessages.push(
        "Each beat isn't being cut into enough pieces to take that one. Try increasing the number of pieces.",
      );
    }
    return { isSuccess, errorMessages };
  },
  errorMessages: [],
});

export const buildRepeatEffect = (): EffectSetting => ({
  title: "Repeat",
  type: "repeat",
  description: "Repeat beats a certain number of times.",
  parameters: [
    { title: "Every", value: 2, jsonKey: "period", minValue: 1 },
    { title: "Offset", value: 0, jsonKey: "offset", minValue: 0 },
    { title: "Times", value: 2, jsonKey: "times", minValue: 1 },
  ],
  isValid: true,
  validate: function () {
    return defaultValidation(this);
  },
  errorMessages: [],
});

export const buildSilenceEffect = (): EffectSetting => ({
  title: "Silence",
  type: "silence",
  description: "Silences beats, but retains their length.",
  parameters: [
    { title: "Every", value: 2, jsonKey: "period", minValue: 2 },
    { title: "Offset", value: 0, jsonKey: "offset", minValue: 0 },
  ],
  isValid: true,
  validate: function () {
    return defaultValidation(this);
  },
  errorMessages: [],
});

export const buildReverseEffect = (): EffectSetting => ({
  title: "Reverse",
  type: "reverse",
  description: "Reverses individual beats.",
  parameters: [
    { title: "Every", value: 2, jsonKey: "period", minValue: 1 },
    { title: "Offset", value: 0, jsonKey: "offset", minValue: 0 },
  ],
  isValid: true,
  validate: function () {
    return defaultValidation(this);
  },
  errorMessages: [],
});

export const buildDefaultBpmSettings = (): BpmSettings => ({
  isManualTempo: false,
  bpm: DEFAULT_BPM,
  isBpmValid: true,
  tolerance: DEFAULT_TOLERANCE,
  isToleranceValid: true,
});
